import React, { useEffect, useState, useMemo } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import {
	Grid,
	Typography,
	useTheme,
	Box,
	Button,
	IconButton,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Chip,
	Divider,
	Breadcrumbs
} from '@mui/material';
import { emphasize, styled } from '@mui/material/styles';
import {
	AddShoppingCartOutlined,
	ArrowBack,
	ExpandMore,
	LocalShipping,
	Recycling,
	Favorite,
	Home as HomeIcon
} from '@mui/icons-material';
import { Rating } from '@mui/material';
import { BackHandler } from 'react-native';
import { ProductsService } from '../../services/supabase/ProductsService';
import { Product } from '../../models/DB';
import { ScreenComponent } from '../../models/enums/ScreenComponents';
import { cartStore } from '../../app/stores/Cart';
import { ImagePreviewer } from '../../components/ImagePreviewer';
import { useStoreConfig } from '../../contexts/StoreConfigContext';
import { navigationStore } from '../../app/stores/Navigation';
import { useIsFocused } from '@react-navigation/native';
import { useTracking } from '../../app/hooks/custom/useTracking';
import { calculateDiscountedPrice } from '../../utils/customFunctions';
import { appConfigStore } from '../../app/stores/AppConfig';
import { ProductMetaTags } from '../../components/ProductMetaTags';
import { productStore } from '../../app/stores/Product';
import { HorizontalScrollSection } from '../../components/HorizontalScrollSection';
import { ProductItem } from '../../components/ProductItem';

// Styled Breadcrumb component based on MUI Chip
const StyledBreadcrumb = styled(Chip)(({ theme }) => {
	const backgroundColor = theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800];
	return {
		backgroundColor,
		height: theme.spacing(3),
		color: theme.palette.text.primary,
		fontWeight: theme.typography.fontWeightRegular,
		'&:hover, &:focus': {
			backgroundColor: emphasize(backgroundColor, 0.06),
		},
		'&:active': {
			boxShadow: theme.shadows[1],
			backgroundColor: emphasize(backgroundColor, 0.12),
		},
	};
}) as typeof Chip;

export const ProductDetail = ({ route, navigation }) => {
	const { trackProductView, trackCartAdd } = useTracking();
	const { config } = useStoreConfig();
	const [selectedVariant, setSelectedVariant] = useState<string>();
	const [loading, setLoading] = useState(true);
	const [product, setProduct] = useState<Product>();
	const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
	const productsService = ProductsService();
	const theme = useTheme();
	const darkMode = appConfigStore((state) => state.darkMode);

	const { updateShowNavigationBar } = navigationStore((state) => state);
	const isFocused = useIsFocused();

	const { addToCart, updateCartItemQuantity, removeFromCart, cartItems } = cartStore((state) => state);
	const allProducts = productStore((state) => state.allProducts);
	const cachedProducts = productStore((state) => state.cachedProducts);
	const isCacheValid = productStore((state) => state.isCacheValid);
	const getCachedProductsByCategory = productStore((state) => state.getCachedProductsByCategory);

	// Silently fetch all products in background if not already loaded
	useEffect(() => {
		// Only fetch if products are empty and cache is stale
		if (allProducts.length === 0 && !isCacheValid()) {
			productsService.getAllProducts();
		}
	}, []);

	// Get similar products (same category, excluding current product)
	// Use full products if available, fall back to cached products
	const similarProducts = useMemo(() => {
		if (!product) return [];

		// Prefer full products if available
		if (allProducts.length > 0) {
			return allProducts
				.filter(p => p.category === product.category && p.id !== product.id && p.isActive !== false)
				.slice(0, 10);
		}

		// Fall back to cached products (lightweight version)
		const cached = getCachedProductsByCategory(product.category, product.id);
		// Convert cached products to Product-like objects for rendering
		return cached.slice(0, 10).map(cp => ({
			id: cp.id,
			name: cp.name,
			category: cp.category,
			price: cp.price,
			discount: cp.discount,
			discountType: cp.discountType,
			inStock: cp.inStock,
			isActive: cp.isActive,
			showRating: cp.showRating,
			thumbnailResizeMode: cp.thumbnailResizeMode as Product['thumbnailResizeMode'],
			code: '',
			description: '',
			stockOnHand: 0,
			variantType: '',
			variantOptions: [],
			created: '',
			updated: '',
			images: cp.thumbnailUrl 
				? { thumbnail: { url: cp.thumbnailUrl, resizeMode: cp.thumbnailResizeMode } } as unknown as Product['images']
				: [] as Product['images']
		}));
	}, [product, allProducts, cachedProducts, getCachedProductsByCategory]);

	const isVariantInCart = product && cartItems.some(item => item.product.code === product.code && item.selectedVariant === selectedVariant);
	const cartQuantity = isVariantInCart ? cartItems.find(item => item.product.code === product.code && item.selectedVariant === selectedVariant)?.quantity : 0;

	const params = route.params;

	useEffect(() => {
		const backAction = () => {
			navigation.navigate(ScreenComponent.Shop);
			return true;
		};
		const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);
		return () => backHandler.remove();
	}, [navigation]);

	useEffect(() => {
		if (params && params.id)
			fetchProductById(params.id)
		else
			setLoading(false);
	}, [params]);

	useEffect(() => {
		if (isFocused) {
			updateShowNavigationBar(true);
		}
	}, [isFocused]);

	const fetchProductById = async (productId: string) => {
		setLoading(true);
		const product = await productsService.getProductById(productId);
		if (product) {
			console.log('📦 [ProductDetail] Loaded product:', {
				name: product.name,
				code: product.code,
				imageKeys: Object.keys(product.images),
				images: product.images
			});
			setProduct(product);
			const images = product.images as unknown as { [key: string]: { url: string; resizeMode?: string } };
			trackProductView(product.id, {
				name: product.name,
				price: product.price,
				category: product.category,
				code: product.code,
				image: images?.thumbnail?.url || images?.main?.url,
				thumbnailResizeMode: images?.thumbnail?.resizeMode ?? product.thumbnailResizeMode ?? 'cover'
			}).catch(error => {
				console.error('Failed to track product view:', error);
			});
		}
		setLoading(false);
	}

	const handleAddToCart = () => {
		if (product) {
			addToCart(product, selectedVariant);
			trackCartAdd(product.id, 1, {
				name: product.name,
				price: product.price,
				variant: selectedVariant
			}).catch(error => {
				console.error('Failed to track cart add:', error);
			});
		}
	};

	const handleUpdateCartQuantity = (quantityChange: number) => {
		if (product) {
			const cartItem = cartItems.find(item => item.product.code === product.code && item.selectedVariant === selectedVariant);
			if (cartItem) {
				updateCartItemQuantity(product.code, cartItem.quantity + quantityChange, selectedVariant);
			}
		}
	};

	const handleRemoveFromCart = () => {
		if (product) {
			removeFromCart(product.code, selectedVariant);
		}
	};

	const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpandedAccordion(isExpanded ? panel : false);
	};

	const finalPrice = product && product.discount > 0
		? parseFloat(calculateDiscountedPrice(product.price, product.discount))
		: product?.price || 0;

	return (
		<>
			{/* Product-specific meta tags for social sharing */}
			{product && <ProductMetaTags product={product} />}

			<ScrollView
				style={{
					backgroundColor: theme.palette.background.default,
				}}
				contentContainerStyle={styles.container}
			>
				{/* Header */}
				<Box sx={{ 
			position: 'sticky', 
			top: 0, 
			zIndex: 10, 
			backgroundColor: theme.palette.background.default, 
			py: { xs: 1.5, md: 2 }, 
			px: 2,
			boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
				<IconButton 
					onClick={() => navigation.navigate(ScreenComponent.Shop)} 
					size="small"
					sx={{ flexShrink: 0 }}
				>
					<ArrowBack />
				</IconButton>

				{product && (
				<Breadcrumbs 
						aria-label="breadcrumb" 
						sx={{ 
							flex: 1, 
							overflow: 'hidden',
							'& .MuiBreadcrumbs-separator': {
								mx: { xs: 0.5, md: 1 }
							}
						}}
					>
						<StyledBreadcrumb
							component="a"
							href="#"
							label="Home"
							icon={<HomeIcon fontSize={'small'} sx={{ display: { xs: 'none', sm: 'block' } }} />}
							onClick={(e) => {
								e.preventDefault();
								navigation.navigate(ScreenComponent.Shop);
							}}
							sx={{ 
								cursor: 'pointer', 
								px: { xs: 0.75, md: 1 }, 
								py: { xs: 0.5, md: 1.5 },
								height: { xs: 'auto', md: theme.spacing(3) },
								fontSize: { xs: '0.65rem', md: '0.75rem' },
								'& .MuiChip-label': {
									px: { xs: 0.5, md: 1 }
								}
							}}
						/>
						<StyledBreadcrumb
							component="a"
							href="#"
							label={product.category}
							onClick={(e) => {
								e.preventDefault();
								navigation.navigate(ScreenComponent.Shop, { category: product.category });
							}}
							sx={{ 
								cursor: 'pointer', 
								px: { xs: 0.75, md: 1 }, 
								py: { xs: 0.5, md: 1.5 },
								height: { xs: 'auto', md: theme.spacing(3) },
								fontSize: { xs: '0.65rem', md: '0.75rem' },
								'& .MuiChip-label': {
									px: { xs: 0.5, md: 1 }
								}
							}}
						/>
						{product.subcategory && (
							<StyledBreadcrumb
								component="a"
								href="#"
								label={product.subcategory}
								onClick={(e) => {
									e.preventDefault();
									navigation.navigate(ScreenComponent.Shop, { category: product.category });
								}}
								sx={{ 
									cursor: 'pointer', 
									px: { xs: 0.75, md: 1 }, 
									py: { xs: 0.5, md: 1.5 },
									height: { xs: 'auto', md: theme.spacing(3) },
									fontSize: { xs: '0.65rem', md: '0.75rem' },
									'& .MuiChip-label': {
										px: { xs: 0.5, md: 1 }
									}
								}}
							/>
						)}
						<StyledBreadcrumb
								label={product.name.length > 20 ? product.name.substring(0, 24) + '...' : product.name}
							sx={{ 
								cursor: 'default', 
								px: { xs: 0.75, md: 1 }, 
								py: { xs: 0.5, md: 1.5 },
								height: { xs: 'auto', md: theme.spacing(3) },
							fontSize: { xs: '0.65rem', md: '0.75rem' },
							backgroundColor: config.primaryColor,
								color: 'white',
								'& .MuiChip-label': {
									px: { xs: 0.5, md: 1 },
									color: 'white'
								}
							}}
						/>
					</Breadcrumbs>
				)}
			</Box>
		</Box>

			{
				!loading && product && (
					<Grid container maxWidth="xl" sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
						{/* Product Images */}
						<Grid item xs={12} md={7}>
							<ImagePreviewer 
								images={product.images as unknown as { [key: string]: { url: string; resizeMode?: string } }}
								defaultResizeMode={product.thumbnailResizeMode as 'cover' | 'contain' | 'center' | 'stretch' | 'repeat' ?? 'cover'}
							/>
						</Grid>

						{/* Product Info */}
						<Grid item xs={12} md={5} sx={{ pl: { md: 6 }, mt: { xs: 4, md: 0 } }}>
						{/* Product Title & Price */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2, fontWeight: 'bold' }}>
								{product.subcategory ? `${product.category} · ${product.subcategory}` : product.category}
							</Typography>
								<Typography 
									variant="h4" 
									fontWeight="bold" 
									sx={{ 
										mb: 1,
										fontFamily: config.primaryFont ? `'${config.primaryFont}', sans-serif` : 'primaryFont'
									}}
								>
									{product.name}
								</Typography>

									{/* Star Rating */}
									{product.showRating !== false && (
										<Rating
											name="read-only"
											value={5}
											readOnly
											size="medium"
											sx={{
												mb: 2,
												'& .MuiRating-iconFilled': {
													color: '#FFC107',
												},
											}}
										/>
									)}

								{/* Price */}
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
									<Typography variant="h5" fontWeight="bold">
										{config.currency}{finalPrice?.toFixed(2)}
									</Typography>
									{product.discount > 0 && (
										<>
												<Typography
													variant="h6"
													color="text.secondary"
													sx={{ textDecoration: 'line-through' }}
												>
													{config.currency}{product.price.toFixed(2)}
												</Typography>
												<Chip
													label={`${product.discount}% OFF`}
													color="error"
													size="small"
													sx={{ fontWeight: 'bold' }}
												/>
											</>
										)}
									</Box>

									{!product.inStock && (
										<Chip
											label="Out of Stock"
											color="error"
											variant="outlined"
											sx={{ mb: 2 }}
										/>
									)}
								</Box>

								{/* Product Description */}
								{product.description && (
									<Box sx={{ mb: 3 }}>
										<Typography variant="body1" color="text.secondary" paragraph>
											{product.description}
										</Typography>
									<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
										<Typography variant="caption" color="text.secondary">
											Code: {product.code.toUpperCase()}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											•
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Category: {product.category}
										</Typography>
										{product.subcategory && (
											<>
												<Typography variant="caption" color="text.secondary">
													•
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Brand: {product.subcategory}
												</Typography>
											</>
										)}
									</Box>
									</Box>
								)}

								<Divider sx={{ my: 3 }} />

								{/* Variant Selector */}
								{product.variantType && product.inStock && (
									<Box sx={{ mb: 4 }}>
										<Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
											Select {product.variantType}
										</Typography>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
											{product.variantOptions?.map((option) => (
												<Button
													key={option}
													variant={selectedVariant === option ? 'contained' : 'outlined'}
													onClick={() => setSelectedVariant(option)}
													sx={{
														minWidth: 60,
														px: 2.5,
														py: 1,
														border: `1.5px solid ${darkMode ? 'white' : theme.palette.divider}`,
														borderRadius: 1,
														backgroundColor: selectedVariant === option ? theme.palette.primary.main : 'transparent',
														color: selectedVariant === option ? 'white' : theme.palette.text.primary,
														'&:hover': {
															backgroundColor: selectedVariant === option ? theme.palette.primary.dark : theme.palette.action.hover,
															borderColor: theme.palette.primary.main,
														}
													}}
												>
													{option}
												</Button>
											))}
										</Box>
										{product.variantType && !selectedVariant && (
											<Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
												Please select a {product.variantType.toLowerCase()}
											</Typography>
										)}
									</Box>
								)}

								{/* Add to Cart / Quantity Control */}
								{product.inStock && (
									isVariantInCart ? (
										<Box sx={{ mb: 2 }}>
											<Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
												Product in cart - Update quantity
											</Typography>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
												<Button
													variant={cartQuantity === 1 ? 'contained' : 'outlined'}
													color={cartQuantity === 1 ? 'error' : 'primary'}
													onClick={cartQuantity === 1 ? handleRemoveFromCart : () => handleUpdateCartQuantity(-1)}
													sx={{ minWidth: 100 }}
												>
													{cartQuantity === 1 ? 'Remove' : '-'}
												</Button>
												<Typography variant="h6" fontWeight="bold" sx={{ minWidth: 40, textAlign: 'center' }}>
													{cartQuantity}
												</Typography>
												<Button
													variant="outlined"
													onClick={() => handleUpdateCartQuantity(1)}
													disabled={!!(product.variantType && !selectedVariant)}
													sx={{ minWidth: 100 }}
												>
													+
												</Button>
											</Box>
										</Box>
									) : (
											<Button
												fullWidth
												variant="contained"
												size="large"
												disabled={!!(product.variantType && !selectedVariant)}
												onClick={handleAddToCart}
												startIcon={<AddShoppingCartOutlined />}
												sx={{
													py: 1.5,
													mb: 2,
													backgroundColor: config.primaryColor,
													'&:hover': {
														backgroundColor: config.primaryColor,
														opacity: 0.9
													},
													'&:disabled': {
														backgroundColor: theme.palette.action.disabledBackground
													}
												}}
											>
												Add to Cart
											</Button>
										)
								)}

								{/* Sustainability Icons */}
								<Box sx={{ display: 'flex', justifyContent: 'space-around', my: 4, py: 3, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)', borderRadius: 2 }}>
									<Box sx={{ textAlign: 'center' }}>
										<Recycling sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
										<Typography variant="caption" display="block">Sustainable</Typography>
										<Typography variant="caption" display="block">Sourcing</Typography>
									</Box>
									<Box sx={{ textAlign: 'center' }}>
										<Favorite sx={{ fontSize: 32, color: theme.palette.error.main, mb: 1 }} />
										<Typography variant="caption" display="block">Cruelty</Typography>
										<Typography variant="caption" display="block">Free</Typography>
									</Box>
									<Box sx={{ textAlign: 'center' }}>
										<LocalShipping sx={{ fontSize: 32, color: theme.palette.info.main, mb: 1 }} />
										<Typography variant="caption" display="block">Eco-Friendly</Typography>
										<Typography variant="caption" display="block">Packaging</Typography>
									</Box>
								</Box>

							<Divider sx={{ my: 3 }} />

							{/* Collapsible Sections - Dynamically rendered from config */}
							<Box>
								{config.productDetailSections
									.filter(section => section.isEnabled)
									.sort((a, b) => a.sortOrder - b.sortOrder)
									.map((section, index) => (
										<React.Fragment key={section.id}>
											{index > 0 && <Divider sx={{ my: 1 }} />}
											<Accordion
												expanded={expandedAccordion === section.id}
												onChange={handleAccordionChange(section.id)}
												elevation={0}
												sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
											>
												<AccordionSummary expandIcon={<ExpandMore />}>
													<Typography variant="subtitle1" fontWeight="bold">
														{section.title}
													</Typography>
												</AccordionSummary>
												<AccordionDetails>
													<Box
														sx={{
															'& p': {
																typography: 'body2',
																color: 'text.secondary',
																mb: 1,
																'&:last-child': { mb: 0 }
															},
															'& strong': {
																fontWeight: 'bold'
															}
														}}
														dangerouslySetInnerHTML={{ __html: section.content }}
													/>
												</AccordionDetails>
											</Accordion>
										</React.Fragment>
									))}
							</Box>
					</Grid>
					</Grid>
				)
			}

			{/* Similar Products Section */}
			{!loading && product && similarProducts.length > 0 && (
				<Box sx={{ mt: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
				<HorizontalScrollSection
					title='Similar Products'
					subtitle={`More from ${product.subcategory || product.category}`}
					gap={16}
				>
						{similarProducts.map((similarProduct) => (
							<ProductItem
								key={similarProduct.id}
								product={similarProduct}
								navigation={navigation}
								darkMode={darkMode}
								width={200}
							/>
						))}
					</HorizontalScrollSection>
				</Box>
			)}
			</ScrollView>
		</>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
});

export default ProductDetail;
